import { Button, Spinner } from '@crm/ui';
import {
  CalendarCheck,
  ChevronLeft,
  ChevronRight,
  Instagram,
  Mail,
  MapPin,
  Phone,
  Sparkles,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useDocumentTitle } from '@/hooks';
import { cn } from '@/lib/utils';
import { type LandingAccent, usePublicLanding } from '../api';
import { BookingModal } from '../components/booking-modal';
import { WhatsAppIcon } from '../components/whatsapp-icon';

/** O acento e a unica variacao de cor: o resto do desenho e sempre o mesmo. */
const ACCENT: Record<LandingAccent, { text: string; bg: string; soft: string }> = {
  VIOLET: {
    text: 'text-violet-700 dark:text-violet-300',
    bg: 'bg-violet-600 hover:bg-violet-600/90',
    soft: 'bg-violet-500/10',
  },
  ROSE: {
    text: 'text-rose-700 dark:text-rose-300',
    bg: 'bg-rose-600 hover:bg-rose-600/90',
    soft: 'bg-rose-500/10',
  },
  EMERALD: {
    text: 'text-emerald-700 dark:text-emerald-300',
    bg: 'bg-emerald-600 hover:bg-emerald-600/90',
    soft: 'bg-emerald-500/10',
  },
  AMBER: {
    text: 'text-amber-700 dark:text-amber-300',
    bg: 'bg-amber-600 hover:bg-amber-600/90',
    soft: 'bg-amber-500/10',
  },
  SLATE: {
    text: 'text-slate-700 dark:text-slate-300',
    bg: 'bg-slate-800 hover:bg-slate-800/90',
    soft: 'bg-slate-500/10',
  },
};

/**
 * Pagina publica do salao: uma pagina unica, no ritmo de site - topo, servicos,
 * sobre e contato. Plana e sem enfeite: foto, tipografia e uma cor de acento.
 */
export function SalonLandingPage() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const landing = usePublicLanding(slug);
  const [booking, setBooking] = useState<{
    unitId: string;
    unitName: string;
    appointmentId?: string;
  } | null>(null);
  const [slide, setSlide] = useState(0);
  const [showAllCategories, setShowAllCategories] = useState(false);

  const slides = landing.data?.slides ?? [];

  // O topo troca de banner sozinho; com um banner so, fica parado.
  useEffect(() => {
    if (slides.length <= 1) return;

    const timer = setInterval(() => setSlide((current) => (current + 1) % slides.length), 6000);

    return () => clearInterval(timer);
  }, [slides.length]);

  // Link enviado no WhatsApp: abre direto no agendamento que o salao comecou.
  const invitedId = searchParams.get('agendamento');

  useEffect(() => {
    if (!invitedId || !landing.data) return;

    const unit = landing.data.units[0];
    setBooking({ unitId: unit?.id ?? '', unitName: unit?.name ?? '', appointmentId: invitedId });
  }, [invitedId, landing.data]);

  useDocumentTitle(landing.data?.company.name ?? 'Agendar horario');

  if (landing.isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Spinner className="h-6 w-6 text-muted-foreground" />
      </div>
    );
  }

  if (landing.isError || !landing.data) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-6 text-center">
        <div className="space-y-2">
          <h1 className="text-2xl font-semibold">Pagina nao encontrada</h1>
          <p className="text-muted-foreground">
            O endereco pode ter mudado ou a pagina ainda nao foi publicada.
          </p>
        </div>
      </div>
    );
  }

  const page = landing.data;
  const accent = ACCENT[page.accent];
  const firstUnit = page.units[0];
  const active = page.slides[slide];
  // Banner sem texto proprio cai no texto da pagina.
  const current = {
    title: active?.title || page.headline,
    subtitle: active?.subtitle || page.tagline || '',
  };
  const hasHeroImage = page.slides.length > 0;
  const whatsappLink = page.whatsapp
    ? `https://wa.me/${page.whatsapp.replace(/\D/g, '')}?text=${encodeURIComponent(page.whatsappMessage ?? '')}`
    : null;

  const openBooking = () => {
    if (!firstUnit) return;
    setBooking({ unitId: firstUnit.id, unitName: firstUnit.name });
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-30 border-b border-border bg-background/85 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-4 py-3">
          <div className="flex min-w-0 items-center gap-2">
            {page.company.logo ? (
              <img src={page.company.logo} alt="" className="h-9 w-9 rounded-lg object-cover" />
            ) : (
              <span
                className={cn(
                  'flex h-9 w-9 items-center justify-center rounded-lg',
                  accent.soft,
                  accent.text,
                )}
              >
                <Sparkles className="h-5 w-5" />
              </span>
            )}
            <span className="truncate text-lg font-semibold">{page.company.name}</span>
          </div>

          <div className="flex shrink-0 items-center gap-2">
            {/* No celular o nome do salao tem prioridade: o atendente vira icone. */}
            {whatsappLink ? (
              <Button variant="outline" size="icon" className="sm:hidden" asChild>
                <a
                  href={whatsappLink}
                  target="_blank"
                  rel="noreferrer"
                  aria-label="Falar com atendente"
                >
                  <WhatsAppIcon className="h-4 w-4" />
                </a>
              </Button>
            ) : null}

            {whatsappLink ? (
              <Button variant="outline" className="hidden sm:inline-flex" asChild>
                <a href={whatsappLink} target="_blank" rel="noreferrer">
                  <WhatsAppIcon className="h-4 w-4" />
                  Falar com atendente
                </a>
              </Button>
            ) : null}

            <Button className={cn('text-white', accent.bg)} onClick={openBooking}>
              <CalendarCheck />
              Agendar
            </Button>
          </div>
        </div>
      </header>
      <main>
        <section className="border-b border-border">
          {/* Celular: a frase vive sobre a foto e as acoes ficam logo abaixo,
              onde o polegar alcanca sem cobrir a imagem. */}
          <div className="lg:hidden">
            <div className="relative h-[24rem] overflow-hidden">
              {page.slides.map((banner, index) => (
                <div
                  key={`mobile-${banner.image}`}
                  aria-hidden
                  className={cn(
                    'absolute inset-0 bg-cover bg-center transition-opacity duration-700',
                    index === slide ? 'opacity-100' : 'opacity-0',
                  )}
                  style={{ backgroundImage: `url(${banner.image})` }}
                />
              ))}

              {/* Setas nos cantos: trocar de banner sem procurar controle. */}
              {page.slides.length > 1 ? (
                <>
                  <button
                    type="button"
                    aria-label="Banner anterior"
                    className="absolute left-1 top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full text-white/70 transition-colors hover:text-white"
                    onClick={() =>
                      setSlide((index) => (index - 1 + page.slides.length) % page.slides.length)
                    }
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>

                  <button
                    type="button"
                    aria-label="Proximo banner"
                    className="absolute right-1 top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full text-white/70 transition-colors hover:text-white"
                    onClick={() => setSlide((index) => (index + 1) % page.slides.length)}
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                </>
              ) : null}

              {/* Sem pointer-events o gradiente ficaria por cima e engoliria
                  o clique das setas. */}
              <div
                className={cn(
                  'pointer-events-none absolute inset-0 flex flex-col justify-end p-6',
                  hasHeroImage && 'bg-gradient-to-t from-black/85 via-black/45 to-transparent',
                )}
              >
                {current.subtitle ? (
                  <p
                    className={cn(
                      'text-[11px] font-semibold uppercase tracking-[0.3em]',
                      hasHeroImage ? 'text-white/75' : 'text-muted-foreground',
                    )}
                  >
                    {current.subtitle}
                  </p>
                ) : null}

                <h1
                  className={cn(
                    'mt-3 text-3xl font-semibold leading-[1.15] tracking-tight',
                    hasHeroImage && 'text-white',
                  )}
                >
                  {current.title}
                </h1>
              </div>
            </div>

            <div className="space-y-4 px-4 py-6">
              {/* Lado a lado: as duas saidas cabem na mesma linha se o segundo
                  rotulo encurtar no celular. */}
              <div className="flex gap-3">
                <Button
                  className={cn('h-12 flex-1 text-base text-white', accent.bg)}
                  onClick={openBooking}
                >
                  <CalendarCheck />
                  Agendar
                </Button>

                {whatsappLink ? (
                  <Button variant="outline" className="h-12 flex-1 text-base" asChild>
                    <a href={whatsappLink} target="_blank" rel="noreferrer">
                      <WhatsAppIcon className="h-4 w-4" />
                      Atendente
                    </a>
                  </Button>
                ) : null}
              </div>

              {page.slides.length > 1 ? (
                <div className="flex items-center justify-center gap-2">
                  {page.slides.map((banner, index) => (
                    <button
                      key={`dot-${banner.image}`}
                      type="button"
                      aria-label={`Banner ${index + 1}`}
                      onClick={() => setSlide(index)}
                      className={cn(
                        'h-1.5 rounded-full transition-all',
                        index === slide ? 'w-6 bg-foreground' : 'w-1.5 bg-muted-foreground/40',
                      )}
                    />
                  ))}
                </div>
              ) : null}
            </div>
          </div>

          {/* Desktop: texto de um lado, foto do outro. */}
          <div className="mx-auto hidden max-w-6xl items-stretch lg:grid lg:grid-cols-2">
            <div className="flex flex-col justify-center px-6 py-24">
              {current.subtitle ? (
                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-muted-foreground">
                  {current.subtitle}
                </p>
              ) : null}

              <h1 className="mt-5 text-5xl font-semibold leading-[1.1] tracking-tight">
                {current.title}
              </h1>

              <div className="mt-8 flex items-center gap-3">
                <Button
                  className={cn('h-12 px-8 text-base text-white', accent.bg)}
                  onClick={openBooking}
                >
                  <CalendarCheck />
                  Agendar horario
                </Button>

                {whatsappLink ? (
                  <Button variant="outline" className="h-12 px-6 text-base" asChild>
                    <a href={whatsappLink} target="_blank" rel="noreferrer">
                      <WhatsAppIcon className="h-4 w-4" />
                      Falar com atendente
                    </a>
                  </Button>
                ) : null}
              </div>

              {page.slides.length > 1 ? (
                <div className="mt-10 flex items-center gap-4">
                  <button
                    type="button"
                    aria-label="Banner anterior"
                    className="flex h-10 w-10 items-center justify-center rounded-full border border-border transition-colors hover:bg-muted"
                    onClick={() =>
                      setSlide((index) => (index - 1 + page.slides.length) % page.slides.length)
                    }
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <span className="text-xs tracking-widest text-muted-foreground">
                    {String(slide + 1).padStart(2, '0')} /{' '}
                    {String(page.slides.length).padStart(2, '0')}
                  </span>
                  <button
                    type="button"
                    aria-label="Proximo banner"
                    className="flex h-10 w-10 items-center justify-center rounded-full border border-border transition-colors hover:bg-muted"
                    onClick={() => setSlide((index) => (index + 1) % page.slides.length)}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              ) : null}
            </div>

            {hasHeroImage ? (
              <div className="relative min-h-[32rem] overflow-hidden">
                {page.slides.map((banner, index) => (
                  <div
                    key={banner.image}
                    aria-hidden={index !== slide}
                    className={cn(
                      'absolute inset-0 bg-cover bg-center transition-opacity duration-700',
                      index === slide ? 'opacity-100' : 'opacity-0',
                    )}
                    style={{ backgroundImage: `url(${banner.image})` }}
                  />
                ))}
              </div>
            ) : null}
          </div>
        </section>

        {/* Servicos: as categorias, nao o cardapio inteiro - o detalhe fica no agendamento. */}
        <section id="servicos" className="border-b border-border">
          <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6">
            <h2 className="text-3xl font-semibold tracking-tight">Servicos</h2>
            <p className="mt-2 max-w-xl text-muted-foreground">
              Escolha por onde comecar; os horarios de cada servico aparecem no agendamento.
            </p>

            {/* O nome fica sobre a base da foto: a imagem manda, o texto so
                assina - e o que da o ar de vitrine, nao de listagem. */}
            {/* No computador a vitrine para em duas linhas cheias - 6 no
                desktop, 4 no tablet; no celular a lista rola inteira. */}
            <ul className="mt-10 flex flex-wrap justify-center gap-5">
              {page.categories.map((category, index) => (
                <li
                  key={category.id}
                  className={cn(
                    // Larguras fixas com o resto centralizado: a linha
                    // incompleta fica no meio, sem buraco a direita.
                    'w-full sm:w-[calc(50%-0.625rem)] lg:w-[calc(33.333%-0.834rem)]',
                    !showAllCategories && index >= 4 && 'sm:max-lg:hidden',
                    !showAllCategories && index >= 6 && 'lg:hidden',
                  )}
                >
                  <div className="group relative aspect-[4/5] overflow-hidden rounded-2xl bg-muted">
                    {category.image ? (
                      <img
                        src={category.image}
                        alt=""
                        loading="lazy"
                        className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                      />
                    ) : (
                      <span className="absolute inset-0 flex items-center justify-center">
                        <Sparkles className={cn('h-8 w-8', accent.text)} />
                      </span>
                    )}

                    {/* O nome assina a foto; as acoes chegam no hover e ficam
                        sempre visiveis no toque, onde nao ha hover. */}
                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 via-black/50 to-transparent p-5 pt-20">
                      <p className="text-2xl font-medium text-white">{category.name}</p>

                      {/* Meio a meio: as duas saidas tem o mesmo peso no cartao. */}
                      <div className="mt-4 grid grid-cols-2 gap-2 transition-all duration-300 sm:translate-y-2 sm:opacity-0 sm:group-hover:translate-y-0 sm:group-hover:opacity-100">
                        <Button
                          className={cn('w-full text-white', accent.bg)}
                          onClick={openBooking}
                        >
                          <CalendarCheck />
                          Agendar
                        </Button>

                        {whatsappLink ? (
                          <Button
                            variant="outline"
                            aria-label={`Falar com atendente sobre ${category.name}`}
                            className="w-full border-white/40 bg-white/10 text-white hover:bg-white/20 hover:text-white"
                            asChild
                          >
                            <a href={whatsappLink} target="_blank" rel="noreferrer">
                              <WhatsAppIcon className="h-4 w-4" />
                              Atendente
                            </a>
                          </Button>
                        ) : null}
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>

            <div className="mt-10 flex flex-col gap-3 sm:flex-row sm:items-center">
              <Button
                className={cn('h-12 text-base text-white sm:px-8', accent.bg)}
                onClick={openBooking}
              >
                <CalendarCheck />
                Agendar horario
              </Button>

              {/* So aparece onde ha corte: no celular a lista ja vem completa. */}
              {!showAllCategories && page.categories.length > 4 ? (
                <Button
                  variant="outline"
                  className="hidden h-12 text-base sm:inline-flex sm:px-6"
                  onClick={() => setShowAllCategories(true)}
                >
                  Ver mais servicos
                </Button>
              ) : null}
            </div>
          </div>
        </section>

        <section>
          <div className="mx-auto grid max-w-6xl gap-10 px-4 py-16 sm:px-6 lg:grid-cols-2">
            <div className="space-y-4">
              <h2 className="text-3xl font-semibold tracking-tight">Onde nos encontrar</h2>
              {/* Sem caixa: so o nome, o endereco e a saida para agendar - que
                  fica sempre abaixo, para o desenho nao mudar com o endereco. */}
              <ul className="divide-y divide-border">
                {page.units.map((unit) => (
                  <li key={unit.id} className="py-4 first:pt-0">
                    <div className="min-w-0">
                      <p className="text-lg font-medium">{unit.name}</p>
                      {unit.address || unit.city ? (
                        <p className="mt-1 flex items-start gap-2 text-sm text-muted-foreground">
                          <MapPin className="mt-0.5 h-4 w-4 shrink-0" />
                          {[unit.address, unit.city].filter(Boolean).join(' - ')}
                        </p>
                      ) : null}
                      {unit.phone ? (
                        <p className="mt-1 flex items-center gap-2 text-sm text-muted-foreground">
                          <Phone className="h-4 w-4" />
                          {unit.phone}
                        </p>
                      ) : null}
                    </div>

                    <Button
                      variant="ghost"
                      className={cn('-ml-3 mt-2', accent.text)}
                      onClick={() => setBooking({ unitId: unit.id, unitName: unit.name })}
                    >
                      <CalendarCheck />
                      Agendar
                    </Button>
                  </li>
                ))}
              </ul>
            </div>

            <div className="space-y-4">
              <h2 className="text-3xl font-semibold tracking-tight">Contato</h2>
              <ul className="space-y-3 text-muted-foreground">
                {page.address ? (
                  <li className="flex items-start gap-2">
                    <MapPin className="mt-0.5 h-4 w-4 shrink-0" />
                    {[page.address, page.city].filter(Boolean).join(' - ')}
                  </li>
                ) : null}
                {page.email ? (
                  <li className="flex items-center gap-2">
                    <Mail className="h-4 w-4" />
                    <a className="hover:underline" href={`mailto:${page.email}`}>
                      {page.email}
                    </a>
                  </li>
                ) : null}
                {whatsappLink ? (
                  <li className="flex items-center gap-2">
                    <WhatsAppIcon className="h-4 w-4" />
                    <a
                      className="hover:underline"
                      href={whatsappLink}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Falar com atendente
                    </a>
                  </li>
                ) : null}
                {page.instagramUrl ? (
                  <li className="flex items-center gap-2">
                    <Instagram className="h-4 w-4" />
                    <a
                      className="hover:underline"
                      href={page.instagramUrl}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Instagram
                    </a>
                  </li>
                ) : null}
              </ul>
            </div>
          </div>
        </section>
      </main>
      <footer className="border-t border-border">
        <div className="mx-auto max-w-6xl px-4 py-8 text-sm text-muted-foreground sm:px-6">
          {page.company.name} - agendamento online
        </div>
      </footer>

      {booking ? (
        <BookingModal
          slug={page.slug}
          unitId={booking.unitId}
          unitName={booking.unitName}
          appointmentId={booking.appointmentId}
          onClose={() => setBooking(null)}
        />
      ) : null}
    </div>
  );
}
